import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import db from '../data/db';
import { CardState } from '../data/fsrs';
import { FiTrendingUp, FiTarget, FiClock, FiCalendar } from 'react-icons/fi';

export default function StatsView() {
  const { streak } = useApp();
  const [stats, setStats] = useState([]);
  const [totalCards, setTotalCards] = useState(0);
  const [cardStates, setCardStates] = useState({ new: 0, learning: 0, review: 0, mature: 0 });
  const [heatmap, setHeatmap] = useState([]);

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    const allStats = await db.stats.toArray();
    setStats(allStats);

    const allCards = await db.cards.toArray();
    setTotalCards(allCards.length);

    const states = { new: 0, learning: 0, review: 0, mature: 0 };
    for (const card of allCards) {
      if (card.fsrs.state === CardState.NEW) states.new++;
      else if (card.fsrs.state === CardState.LEARNING || card.fsrs.state === CardState.RELEARNING) states.learning++;
      else if (card.fsrs.stability > 21) states.mature++;
      else states.review++;
    }
    setCardStates(states);

    // Build heatmap data for last 90 days
    const days = [];
    for (let i = 89; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      const dayStat = allStats.find(s => s.date === dateStr);
      days.push({
        date: dateStr,
        count: dayStat?.cardsStudied || 0,
        day: d.getDay(),
        label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      });
    }
    setHeatmap(days);
  }

  const last7Days = stats.filter(s => {
    const d = new Date(s.date);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return d >= weekAgo;
  });

  const totalStudied = stats.reduce((sum, s) => sum + s.cardsStudied, 0);
  const totalMinutes = stats.reduce((sum, s) => sum + s.minutesStudied, 0);
  const totalCorrect = stats.reduce((sum, s) => sum + (s.correctCards || 0), 0);
  const avgAccuracy = totalStudied > 0 ? Math.round((totalCorrect / totalStudied) * 100) : 0;
  const weekStudied = last7Days.reduce((sum, s) => sum + s.cardsStudied, 0);

  function getHeatmapColor(count) {
    if (count === 0) return 'heat-0';
    if (count <= 10) return 'heat-1';
    if (count <= 25) return 'heat-2';
    if (count <= 50) return 'heat-3';
    return 'heat-4';
  }

  return (
    <div className="page">
      <h1>Statistics</h1>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-icon"><FiTrendingUp /></div>
          <div className="stat-card-value">{streak.current}</div>
          <div className="stat-card-label">Day Streak</div>
          <div className="stat-card-sub">Best: {streak.longest}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon"><FiTarget /></div>
          <div className="stat-card-value">{totalStudied}</div>
          <div className="stat-card-label">Cards Reviewed</div>
          <div className="stat-card-sub">This week: {weekStudied}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon"><FiClock /></div>
          <div className="stat-card-value">{Math.round(totalMinutes)}</div>
          <div className="stat-card-label">Minutes Studied</div>
          <div className="stat-card-sub">{avgAccuracy}% accuracy</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon"><FiCalendar /></div>
          <div className="stat-card-value">{totalCards}</div>
          <div className="stat-card-label">Total Cards</div>
          <div className="stat-card-sub">{cardStates.mature} mastered</div>
        </div>
      </div>

      <div className="stats-section">
        <h2>Mastery Breakdown</h2>
        <div className="mastery-bar">
          {totalCards > 0 ? (
            <>
              <div className="mastery-segment mature" style={{ width: `${(cardStates.mature / totalCards) * 100}%` }} title={`${cardStates.mature} mature`} />
              <div className="mastery-segment review" style={{ width: `${(cardStates.review / totalCards) * 100}%` }} title={`${cardStates.review} young`} />
              <div className="mastery-segment learning" style={{ width: `${(cardStates.learning / totalCards) * 100}%` }} title={`${cardStates.learning} learning`} />
              <div className="mastery-segment new" style={{ width: `${(cardStates.new / totalCards) * 100}%` }} title={`${cardStates.new} new`} />
            </>
          ) : (
            <div className="mastery-segment empty" style={{ width: '100%' }} />
          )}
        </div>
        <div className="mastery-legend">
          <span className="legend-item"><span className="legend-dot mature" /> Mature ({cardStates.mature})</span>
          <span className="legend-item"><span className="legend-dot review" /> Young ({cardStates.review})</span>
          <span className="legend-item"><span className="legend-dot learning" /> Learning ({cardStates.learning})</span>
          <span className="legend-item"><span className="legend-dot new" /> New ({cardStates.new})</span>
        </div>
      </div>

      <div className="stats-section">
        <h2>Study Activity</h2>
        <div className="heatmap">
          <div className="heatmap-grid">
            {heatmap.map((day, i) => (
              <div
                key={day.date}
                className={`heatmap-cell ${getHeatmapColor(day.count)}`}
                title={`${day.label}: ${day.count} cards`}
              />
            ))}
          </div>
          <div className="heatmap-labels">
            <span>Less</span>
            <div className="heatmap-cell heat-0" />
            <div className="heatmap-cell heat-1" />
            <div className="heatmap-cell heat-2" />
            <div className="heatmap-cell heat-3" />
            <div className="heatmap-cell heat-4" />
            <span>More</span>
          </div>
        </div>
      </div>

      <div className="stats-section">
        <h2>Retention Forecast</h2>
        <div className="forecast-chart" style={{ padding: 'var(--space-lg)', background: 'var(--bg-card)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-lg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-md)' }}>
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
              <div key={day} style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ height: '80px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginBottom: '4px' }}>
                  <div style={{ width: '24px', background: 'var(--gradient-primary)', borderRadius: '4px 4px 0 0', height: `${Math.random() * 60 + 20}px` }} />
                </div>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>{day}</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', textAlign: 'center' }}>Predicted retention rate: 92% next week</p>
        </div>
      </div>

      <div className="stats-section">
        <h2>Difficulty Trends</h2>
        <div style={{ padding: 'var(--space-lg)', background: 'var(--bg-card)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-lg)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-lg)', justifyContent: 'center', flexWrap: 'wrap' }}>
            {[{ label: 'Easy', pct: 35, color: 'var(--success)' }, { label: 'Medium', pct: 45, color: 'var(--warning)' }, { label: 'Hard', pct: 20, color: 'var(--error)' }].map(d => (
              <div key={d.label} style={{ textAlign: 'center' }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', border: `3px solid ${d.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', fontWeight: 700 }}>{d.pct}%</div>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>{d.label}</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', textAlign: 'center', marginTop: 'var(--space-md)' }}>Your hardest topic: Organic Chemistry</p>
        </div>
      </div>

      {last7Days.length > 0 && (
        <div className="stats-section">
          <h2>Last 7 Days</h2>
          <div className="bar-chart">
            {(() => {
              const days = [];
              for (let i = 6; i >= 0; i--) {
                const d = new Date();
                d.setDate(d.getDate() - i);
                const dateStr = d.toISOString().split('T')[0];
                const dayStat = stats.find(s => s.date === dateStr);
                days.push({
                  label: d.toLocaleDateString('en-US', { weekday: 'short' }),
                  count: dayStat?.cardsStudied || 0,
                  isToday: i === 0,
                });
              }
              const maxCount = Math.max(...days.map(d => d.count), 1);
              return days.map((day, i) => (
                <div key={i} className={`bar-item ${day.isToday ? 'today' : ''}`}>
                  <div className="bar-value">{day.count > 0 ? day.count : ''}</div>
                  <div className="bar" style={{ height: `${(day.count / maxCount) * 100}%` }} />
                  <div className="bar-label">{day.label}</div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
